import { FC, ReactNode, useState } from 'react';

import { ChevronDownIcon, EyeIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '../../../../components/combobox/index';
import { Input } from '../../../../components/input/index';
import { Switch } from '../../../../components/switch/index';
import {
  SharedCapTableExpireInterval,
  SharedCapTableSettings,
} from '../../../../hooks/shared-cap-table/shared-cap-table.types';
import { useLocale } from '../../../../hooks/use-locale.hook';
import { StringKey } from '../../../../lang/index';
import { resolveSharePasswordState } from '../share-password-state.util';
import { ExpandingFields } from './expanding-fields';

type PrivacyTabProps = {
  /** `undefined` — untouched, the link keeps `storedExpiresAt`. See `ShareLinkFormValues`. */
  expireInterval: null | SharedCapTableExpireInterval | undefined;
  hasStoredPassword: boolean;
  isEditMode: boolean;
  onAllowExportChange: (settings: SharedCapTableSettings) => void;
  onExpireIntervalChange: (interval: null | SharedCapTableExpireInterval) => void;
  onPasswordChange: (password: null | string) => void;
  password: null | string;
  settings: SharedCapTableSettings;
  storedExpiresAt: null | string;
};

/**
 * Stands in for the password already on the link. A bcrypt hash cannot be turned back into what the
 * owner typed, so the field opens filled with this rather than empty — an empty box read as "the
 * password is gone" when leaving it untouched is exactly what keeps it.
 */
const STORED_PASSWORD_MASK = '••••••••••••';

/** `Immediately` revokes an existing link, so it only makes sense while editing one. */
const CREATE_INTERVALS: [SharedCapTableExpireInterval, StringKey][] = [
  [SharedCapTableExpireInterval.ONE_DAY, 'SHARE_INTERVAL_ONE_DAY'],
  [SharedCapTableExpireInterval.THREE_DAYS, 'SHARE_INTERVAL_THREE_DAYS'],
  [SharedCapTableExpireInterval.SEVEN_DAYS, 'SHARE_INTERVAL_SEVEN_DAYS'],
  [SharedCapTableExpireInterval.TWO_WEEKS, 'SHARE_INTERVAL_TWO_WEEKS'],
  [SharedCapTableExpireInterval.ONE_MONTH, 'SHARE_INTERVAL_ONE_MONTH'],
];

const EDIT_INTERVALS: [SharedCapTableExpireInterval, StringKey][] = [
  ...CREATE_INTERVALS,
  [SharedCapTableExpireInterval.IMMEDIATELY, 'SHARE_INTERVAL_IMMEDIATELY'],
];

/** Header row plus the white pane its fields sit on, shared by the password and expiry settings. */
const SettingCard: FC<{
  checked: boolean;
  children: ReactNode;
  onChange: (state: boolean) => void;
  title: StringKey;
}> = ({ checked, children, onChange, title }) => (
  // Not clipped: the expiry section holds a dropdown that opens past the card's own bounds, and the
  // inner pane carries its own rounding, so nothing needs trimming here.
  <div className="flex flex-col gap-4 rounded-md bg-gray-50 px-1 pb-1 pt-4">
    <div className="flex items-center justify-between gap-4 px-3">
      <span className="text-xs font-[450] leading-4 text-gray-700">
        <AppFormattedMessage id={title} />
      </span>
      <Switch checked={checked} onChange={onChange} />
    </div>

    <ExpandingFields isOpen={checked}>
      <div className="flex flex-col gap-1.5 rounded-md bg-white p-3 shadow-xs">{children}</div>
    </ExpandingFields>
  </div>
);

export const PrivacyTab: FC<PrivacyTabProps> = ({
  expireInterval,
  hasStoredPassword,
  isEditMode,
  onAllowExportChange,
  onExpireIntervalChange,
  onPasswordChange,
  password,
  settings,
  storedExpiresAt,
}) => {
  const { messagesLocale } = useLocale();
  const [isPasswordEnabled, setPasswordEnabled] = useState(password !== null);
  // The field opens empty by design, which already fails the minimum — the message waits until the
  // owner has actually typed or left the field, so switching protection on does not read as an error.
  const [isPasswordTouched, setPasswordTouched] = useState(false);
  const [isPasswordRevealed, setPasswordRevealed] = useState(false);
  // Until the owner puts the cursor in the field, it shows the stand-in for the stored password.
  const [isPasswordEdited, setPasswordEdited] = useState(!hasStoredPassword);
  // Untouched (`undefined`) means the link keeps whatever it already has, so the toggle has to read
  // the stored date — starting it off would misreport an expiring link as permanent.
  const [isExpiryEnabled, setExpiryEnabled] = useState(
    expireInterval === undefined ? storedExpiresAt !== null : expireInterval !== null,
  );
  const intervals = isEditMode ? EDIT_INTERVALS : CREATE_INTERVALS;

  const handlePasswordToggle = (state: boolean) => {
    setPasswordEnabled(state);
    setPasswordTouched(false);
    setPasswordRevealed(false);
    setPasswordEdited(!hasStoredPassword);
    onPasswordChange(state ? '' : null);
  };

  /** Empty again means "keep the stored one", so the stand-in comes back rather than a blank box. */
  const handlePasswordBlur = () => {
    setPasswordTouched(true);

    if (hasStoredPassword && !password) {
      setPasswordEdited(false);
      setPasswordRevealed(false);
    }
  };

  const handleExpiryToggle = (state: boolean) => {
    setExpiryEnabled(state);
    onExpireIntervalChange(state ? SharedCapTableExpireInterval.SEVEN_DAYS : null);
  };

  const selectedIntervalTitle =
    intervals.find(([interval]) => interval === expireInterval)?.[1] ?? 'SHARE_INTERVAL_SEVEN_DAYS';

  const { isBlank, isIncomplete } = resolveSharePasswordState({ hasStoredPassword, password });
  const isPasswordIncomplete = isPasswordEnabled && isPasswordTouched && isIncomplete;

  return (
    // A card per setting rather than one shared card: each row expands into its own fields when
    // switched on, and the design keeps that expansion inside the row's own surface.
    <div className="flex flex-col gap-2">
      <SettingCard
        checked={isPasswordEnabled}
        onChange={handlePasswordToggle}
        title="SHARE_PASSWORD_PROTECTION"
      >
        {/* The shared input already is the design's field — floating caption, grey fill and a
            single hairline — so the eye rides in its icon slot rather than a hand-built shell. */}
        <Input
          autoComplete="new-password"
          // Focusing an untouched field would wipe the stand-in the owner just came to look at.
          autoFocus={isPasswordEdited}
          icon={
            isPasswordEdited ? (
              <EyeIcon variant={isPasswordRevealed ? 'ACTIVE' : 'CLOSE'} />
            ) : undefined
          }
          onBlur={handlePasswordBlur}
          onChange={(value) => {
            setPasswordTouched(true);
            onPasswordChange(value);
          }}
          onFocus={() => setPasswordEdited(true)}
          onIconClick={() => setPasswordRevealed((revealed) => !revealed)}
          placeholder={<AppFormattedMessage id="PASSWORD" />}
          type={isPasswordEdited && isPasswordRevealed ? 'text' : 'password'}
          value={isPasswordEdited ? (password ?? '') : STORED_PASSWORD_MASK}
          wrapperClassName="w-full"
        />

        {isEditMode && hasStoredPassword && (
          <span className="text-xs font-[450] text-gray-400">
            <AppFormattedMessage id="SHARE_PASSWORD_KEEP_HINT" />
          </span>
        )}
        {isPasswordIncomplete && (
          <span className="text-xs font-[450] text-fireside-600">
            <AppFormattedMessage
              id={isBlank ? 'SHARE_PASSWORD_BLANK' : 'SHARE_PASSWORD_MIN_LENGTH'}
            />
          </span>
        )}
      </SettingCard>

      <SettingCard
        checked={isExpiryEnabled}
        onChange={handleExpiryToggle}
        title="SHARE_EXPIRE_LINK"
      >
        {/* Which interval produced the stored date is not recoverable, so show the date. */}
        {expireInterval === undefined && storedExpiresAt && (
          <span className="text-xs font-[450] text-gray-400">
            <AppFormattedMessage
              id="SHARE_EXPIRE_CURRENT"
              values={{ date: new Date(storedExpiresAt).toLocaleDateString('en-GB') }}
            />
          </span>
        )}

        {/* Same combobox the dividend steps use: the app's select is this, not a bare button. */}
        <Combobox
          as="div"
          className="relative"
          onChange={(interval: SharedCapTableExpireInterval) => onExpireIntervalChange(interval)}
          value={expireInterval ?? SharedCapTableExpireInterval.SEVEN_DAYS}
        >
          <div className="relative">
            <ComboboxInput
              icon={<ChevronDownIcon className="size-4" />}
              onChange={() => undefined}
              placeholder={<AppFormattedMessage id="SHARE_INTERVAL" />}
              readOnly
              value={messagesLocale[selectedIntervalTitle]}
            />
            <ComboboxButton className="absolute inset-0 z-1 size-full cursor-pointer" />
          </div>
          {/* Rendered in place, exactly as the dividend steps do. Lifting the list out — by our own
              portal or Headless UI's anchor — put the options outside the combobox's own subtree,
              and choosing one read as a click elsewhere. The card no longer clips, so it fits. */}
          <ComboboxOptions className="z-[62] w-full gap-0.5 p-1">
            {intervals.map(([interval, title]) => (
              <ComboboxOption key={interval} value={interval}>
                <span className="block text-sm text-gray-700">
                  <AppFormattedMessage id={title} />
                </span>
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        </Combobox>
      </SettingCard>

      <div className="flex h-[52px] items-center justify-between gap-4 rounded-lg bg-gray-50 px-4">
        <span className="text-xs font-[450] leading-4 text-gray-700">
          <AppFormattedMessage id="SHARE_ALLOW_EXPORT" />
        </span>
        <Switch
          checked={settings.allowExport}
          onChange={(state) => onAllowExportChange({ ...settings, allowExport: state })}
        />
      </div>
    </div>
  );
};
